import { getNodeName } from "@/workflow_api/modals/node-search";
import { type RefName } from "@/workflow_api/types/workflow";
import {
  Avatar,
  AvatarFallback,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@elaniintc/goes-design-system";

interface Props {
  node_type: RefName;
  node_name: string;
  status: string;
  status_label: string;
}

const renderNode = [
  "form-node",
  "revision-node",
  "payment-node",
  "sign-document-node",
  "resolution-node",
];

const statusIcon = (
  node_type: RefName,
  status: string,
): {
  name: string;
  type: string;
  color: "info" | "warning" | "action" | "success" | "danger" | "neutral";
} => {
  if (node_type === "form-node") {
    if (status === "PENDING") {
      return {
        name: "bars",
        type: "fas",
        color: "info",
      };
    }
    if (status === "EXTERNAL_FORM_PENDING_ENTRY") {
      return {
        name: "bars",
        type: "fas",
        color: "info",
      };
    }
    if (status === "IN_PROCESS") {
      return {
        name: "bars",
        type: "fas",
        color: "action",
      };
    }
    if (status === "EXTERNAL_FORM_RECEIVED") {
      return {
        name: "bars",
        type: "fas",
        color: "action",
      };
    }
  }

  // Nodo de revisión
  if (node_type === "revision-node") {
    if (status === "PENDING") {
      return {
        name: "file-circle-info",
        type: "fas",
        color: "info",
      };
    }
    if (
      status === "UNDER_REVIEW" ||
      status === "IN_PROGRESS" ||
      status === "REVIEW_REVIEWING_CORRECTIONS"
    ) {
      return {
        name: "file-edit",
        type: "fas",
        color: "action",
      };
    }
    if (status === "IN_LEADER_REVIEW" || status === "REVIEW_SENT_TO_REVIEW") {
      return {
        name: "clock",
        type: "far",
        color: "warning",
      };
    }
    if (status === "WITH_CORRECTIONS") {
      return {
        name: "send",
        type: "fas",
        color: "warning",
      };
    }
    if (status === "REVIEW_CORRECTION_IN_PROGRESS") {
      return {
        name: "file-edit",
        type: "fas",
        color: "action",
      };
    }
    if (status === "REVIEW_CORRECTIONS_RECEIVED") {
      return {
        name: "file-circle-check",
        type: "fas",
        color: "warning",
      };
    }
    if (status === "EXTENSION_REQUEST") {
      return {
        name: "timer",
        type: "fas",
        color: "warning",
      };
    }
    if (status === "SUSPENSION_REQUEST") {
      return {
        name: "circle-stop",
        type: "fas",
        color: "warning",
      };
    }
    if (status === "SUSPENDED") {
      return {
        name: "circle-stop",
        type: "fas",
        color: "neutral",
      };
    }
    if (status === "IN_PROCESS") {
      return {
        name: "file-edit",
        type: "fas",
        color: "action",
      };
    }
    if (status === "ON_EXTENSION") {
      return {
        name: "timer",
        type: "fas",
        color: "warning",
      };
    }
  }

  if (node_type === "payment-node") {
    if (status === "PAYMENT_PENDING") {
      return {
        name: "dollar",
        type: "fas",
        color: "info",
      };
    }
  }

  if (node_type === "sign-document-node") {
    if (status === "SIGNATURE_PENDING") {
      return {
        name: "signature",
        type: "fas",
        color: "info",
      };
    }
    if (status === "SIGNATURE_IN_PROGRESS") {
      return {
        name: "signature",
        type: "fas",
        color: "action",
      };
    }
  }

  if (node_type === "resolution-node") {
    if (status === "APPROVED") {
      return {
        name: "check",
        type: "fas",
        color: "success",
      };
    }
    if (status === "REJECTED") {
      return {
        name: "times",
        type: "fas",
        color: "danger",
      };
    }
    if (status === "CANCELED") {
      return {
        name: "minus",
        type: "fas",
        color: "neutral",
      };
    }
  }

  if (node_type === "finish-node") {
    if (status === "APPROVED") {
      return {
        name: "check",
        type: "fas",
        color: "success",
      };
    }
    if (status === "REJECTED") {
      return {
        name: "times",
        type: "fas",
        color: "danger",
      };
    }
    if (status === "CANCELED") {
      return {
        name: "minus",
        type: "fas",
        color: "neutral",
      };
    }

    if (status === "FORCE_COMPLETED") {
      return {
        name: "check",
        type: "fas",
        color: "success",
      };
    }
  }

  return {
    name: "arrows-rotate",
    type: "fas",
    color: "neutral",
  };
};

const RequestStatus = (props: Props) => {
  const iconProps = statusIcon(props.node_type, props.status);
  const isRenderNode = renderNode.includes(props.node_type);

  return (
    <div className="flex gap-2 items-center">
      <Tooltip>
        <TooltipTrigger asChild>
          <Avatar size="2xs">
            <AvatarFallback
              color={iconProps?.color || "default"}
              type="icon"
              className="flex items-center justify-center"
            >
              <Icon
                type={iconProps?.type || "fas"}
                name={iconProps?.name || "file-circle-info"}
                className="text-sm"
              />
            </AvatarFallback>
          </Avatar>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-130">
          <p className="text-sm font-semibold text-heading-quiet text-wrap">
            Paso:{" "}
            <span className="text-body-quiet font-normal">
              {getNodeName(props.node_type)}
            </span>
          </p>
          <p className="text-sm font-semibold text-heading-quiet text-wrap">
            Nombre:{" "}
            <span className="text-body-quiet font-normal">
              {props.node_name}
            </span>
          </p>
        </TooltipContent>
      </Tooltip>

      <div className="flex flex-col gap-1 max-w-37.5">
        <p className="text-body font-normal truncate">
          {isRenderNode ? props.node_name : "Acciones del sistema"}
        </p>
        <p className="text-body-quiet text-xs font-normal truncate">
          {props.status_label}
        </p>
      </div>
    </div>
  );
};

export default RequestStatus;
